import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
// 1. Import gambar lokal banner.webp
import bannerLocal from "/banner.webp";

const optimizeCloudinary = (url: string, width = 600) => {
  if (!url || !url.includes("cloudinary.com")) return url;
  return url.replace(
    "/upload/",
    `/upload/f_auto,q_auto:eco,c_fill,w_${width},ar_3:4/`,
  );
};

const SHEET_CSV_URL =
  "https://docs.google.com/spreadsheets/d/e/2PACX-1vTzi-kNn7DGxG8j0n9NHViFKtNLWSCFr-JIFvrmYGHNCAmU5T9fXJtlR-tWTKa9wH3fk8rmQjK6LEYk/pub?gid=0&single=true&output=csv";

const fetchSheetData = async () => {
  try {
    const response = await fetch(SHEET_CSV_URL);
    const csvText = await response.text();
    const rows = csvText.split("\n").filter((row) => row.trim() !== "");
    const headers = rows[0].split(",").map((h) => h.trim());

    return rows.slice(1).map((row) => {
      const values = row.split(",");
      const obj: any = {};
      headers.forEach((header, i) => {
        obj[header] = values[i]?.trim();
      });
      return obj;
    });
  } catch (error) {
    console.error("Gagal mengambil data:", error);
    return [];
  }
};

export const HeroSlider: React.FC = () => {
  const [current, setCurrent] = useState(0);
  // 2. Set default state langsung dengan gambar lokal sebagai index ke-0
  const [heroImages, setHeroImages] = useState<string[]>([bannerLocal]);

  useEffect(() => {
    fetchSheetData().then((data) => {
      const filtered = data
        .filter((item) => item.Kategori === "Hero")
        .map((item) => item["URL Gambar"]);

      if (filtered.length > 0) {
        // 3. Selipkan gambar lokal di depan, diikuti gambar dari Google Sheets (jika ada duplikat, filter)
        // Disini kita asumsikan baris pertama di Sheets adalah versi Cloudinary dari banner tersebut,
        // jadi kita ambil gambar dari Sheets mulai dari index ke-1 atau seterusnya jika ingin full dinamis di belakang.
        setHeroImages([bannerLocal, ...filtered.slice(1)]);
      }
    });
  }, []);

  useEffect(() => {
    if (heroImages.length <= 1) return;
    const timer = setInterval(
      () => setCurrent((prev) => (prev + 1) % heroImages.length),
      5000,
    );
    return () => clearInterval(timer);
  }, [heroImages.length]);

  // Hapus block "if (heroImages.length === 0)" karena sekarang state awal sudah punya 1 gambar lokal (tidak akan blank/pulse)

  return (
    <div
      className="relative w-full overflow-hidden rounded-[2.5rem] sm:rounded-[4rem] shadow-2xl group bg-gray-200"
      style={{ aspectRatio: "3 / 4" }}
    >
      {heroImages.map((img, idx) => (
        <div
          key={idx}
          className={`absolute inset-0 transition-all duration-1000 ease-in-out transform ${
            idx === current
              ? "opacity-100 scale-100"
              : "opacity-0 scale-105 pointer-events-none"
          }`}
        >
          <img
            // 4. Kondisi Src: jika index 0 pakai gambar lokal, selain itu pakai Cloudinary
            src={
              idx === 0
                ? img // Gambar lokal tidak perlu di-optimizeCloudinary karena sudah format webp lokal
                : optimizeCloudinary(img, 1000)
            }
            // 5. Kondisi SrcSet: index 0 tidak butuh srcset Cloudinary jika lokalnya sudah dapet kompresi pas
            srcSet={
              idx === 0
                ? undefined
                : `${optimizeCloudinary(img, 600)} 600w, ${optimizeCloudinary(img, 1000)} 1000w`
            }
            sizes={idx === 0 ? undefined : "(max-width: 640px) 100vw, 1000px"}
            className="w-full h-full object-cover"
            alt={`Promo Utama Hisana Fried Chicken Padang ${idx + 1}`}
            crossOrigin={idx === 0 ? undefined : "anonymous"}
            width="600"
            height="800"
            // 6. Maksimalkan prioritas load untuk gambar lokal pertama
            loading={idx === 0 ? "eager" : "lazy"}
            fetchPriority={idx === 0 ? "high" : "low"}
            onError={(e) => {
              if (idx !== 0) {
                (e.target as HTMLImageElement).src = img;
              }
            }}
          />
        </div>
      ))}

      {/* Navigasi - Hanya muncul kalau gambar lebih dari 1 */}
      {heroImages.length > 1 && (
        <>
          <button
            onClick={() =>
              setCurrent(
                (prev) => (prev - 1 + heroImages.length) % heroImages.length,
              )
            }
            aria-label="Gambar Sebelumnya"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-black/20 hover:bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            onClick={() => setCurrent((prev) => (prev + 1) % heroImages.length)}
            aria-label="Gambar Berikutnya"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-black/20 hover:bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all"
          >
            <ChevronRight size={28} />
          </button>
        </>
      )}

      {/* Indikator - Hanya muncul kalau gambar lebih dari 1 */}
      {heroImages.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex gap-2">
          {heroImages.map((_, idx) => (
            <button
              key={idx}
              className={`h-2 transition-all rounded-full ${idx === current ? "w-8 bg-white" : "w-2 bg-white/40"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
